<script setup lang="ts">
import { computed } from 'vue'
import type { WalkForwardFoldData } from '../api/client'

const props = defineProps<{
  folds: WalkForwardFoldData[]
  meanOosScore: number
  stdOosScore: number
  scoreType: string
}>()

const padLeft = 50
const padRight = 20
const padTop = 10
const padBottom = 35
const chartHeight = 200
const chartWidth = 600

const chartData = computed(() => {
  const validFolds = props.folds.filter(f => f.trainScore != null && f.testScore != null)
  if (validFolds.length === 0) return null

  const allVals = validFolds.flatMap(f => [f.trainScore ?? 0, f.testScore ?? 0])
  const maxVal = Math.max(...allVals, 0.01)
  const minVal = Math.min(...allVals, 0)

  const barAreaWidth = chartWidth - padLeft - padRight
  const groupWidth = barAreaWidth / validFolds.length
  const barWidth = groupWidth * 0.35
  const gap = groupWidth * 0.05
  const innerHeight = chartHeight - padTop - padBottom

  const scale = (v: number) => padTop + innerHeight - ((v - minVal) / (maxVal - minVal)) * innerHeight
  const baseline = scale(0)

  const bars = validFolds.map((f, i) => {
    const x = padLeft + i * groupWidth
    const trainY = scale(f.trainScore ?? 0)
    const testY = scale(f.testScore ?? 0)
    return {
      fold: f.fold,
      trainX: x + gap,
      trainY: Math.min(trainY, baseline),
      trainH: Math.abs(baseline - trainY),
      testX: x + gap + barWidth + gap,
      testY: Math.min(testY, baseline),
      testH: Math.abs(baseline - testY),
      labelX: x + groupWidth / 2,
      trainScore: f.trainScore ?? 0,
      testScore: f.testScore ?? 0,
    }
  })

  // Y-axis ticks
  const range = maxVal - minVal
  const step = range > 0 ? Math.pow(10, Math.floor(Math.log10(range))) : 0.1
  const ticks: { y: number; label: string }[] = []
  for (let v = Math.ceil(minVal / step) * step; v <= maxVal; v += step) {
    ticks.push({ y: scale(v), label: v.toFixed(2) })
  }

  return { bars, barWidth, baseline, ticks, viewBox: `0 0 ${chartWidth} ${chartHeight}` }
})

function fmtScore(v: number): string {
  return v.toFixed(4)
}
</script>

<template>
  <div class="wf-chart-wrap">
    <div class="section-label">Walk-Forward Fold Results</div>

    <svg v-if="chartData" :viewBox="chartData.viewBox" class="wf-svg" preserveAspectRatio="xMidYMid meet">
      <!-- Y-axis ticks -->
      <template v-for="t in chartData.ticks" :key="t.label">
        <line :x1="padLeft - 4" :y1="t.y" :x2="chartWidth - padRight" :y2="t.y" stroke="#222" stroke-width="0.5" />
        <text :x="padLeft - 8" :y="t.y + 3" text-anchor="end" fill="#666" font-size="8">{{ t.label }}</text>
      </template>

      <!-- Baseline -->
      <line :x1="padLeft" :y1="chartData.baseline" :x2="chartWidth - padRight" :y2="chartData.baseline" stroke="#444" stroke-width="1" />

      <!-- Bars -->
      <template v-for="b in chartData.bars" :key="b.fold">
        <!-- Train bar (solid green) -->
        <rect :x="b.trainX" :y="b.trainY" :width="chartData.barWidth" :height="Math.max(b.trainH, 1)" fill="#4caf50" rx="1" />
        <!-- Test bar (blue) -->
        <rect :x="b.testX" :y="b.testY" :width="chartData.barWidth" :height="Math.max(b.testH, 1)" fill="#2196f3" rx="1" />
        <!-- Fold label -->
        <text :x="b.labelX" :y="chartHeight - 10" text-anchor="middle" fill="#888" font-size="9">F{{ b.fold }}</text>
      </template>
    </svg>

    <!-- Legend -->
    <div class="wf-legend">
      <span class="legend-item"><span class="legend-swatch" style="background:#4caf50;"></span> Train</span>
      <span class="legend-item"><span class="legend-swatch" style="background:#2196f3;"></span> Test (OOS)</span>
    </div>

    <!-- Summary -->
    <div class="wf-summary">
      Mean OOS {{ scoreType === 'fitness' ? 'Fitness' : 'Sharpe' }}:
      <span class="mono" :class="meanOosScore > 0 ? 'positive' : 'negative'">{{ fmtScore(meanOosScore) }}</span>
      +/- <span class="mono">{{ fmtScore(stdOosScore) }}</span>
    </div>

    <!-- Fold detail table -->
    <table class="fold-table">
      <thead>
        <tr>
          <th>Fold</th>
          <th>Train Period</th>
          <th>Test Period</th>
          <th>Train Score</th>
          <th>Test Score</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="f in folds" :key="f.fold">
          <td>{{ f.fold }}</td>
          <td class="date-cell">{{ f.trainStart }} &rarr; {{ f.trainEnd }}</td>
          <td class="date-cell">{{ f.testStart }} &rarr; {{ f.testEnd }}</td>
          <td class="mono">{{ f.trainScore != null ? fmtScore(f.trainScore) : '--' }}</td>
          <td class="mono" :class="(f.testScore ?? 0) > 0 ? 'positive' : 'negative'">{{ f.testScore != null ? fmtScore(f.testScore) : '--' }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.wf-chart-wrap { margin-bottom: 16px; }
.section-label { font-size: 9px; color: #666; text-transform: uppercase; letter-spacing: 1px; padding-bottom: 4px; border-bottom: 1px solid #222; margin-bottom: 8px; }
.wf-svg { width: 100%; max-height: 200px; }
.wf-legend { display: flex; gap: 16px; padding: 6px 0; font-size: 11px; color: #888; }
.legend-item { display: flex; align-items: center; gap: 4px; }
.legend-swatch { width: 12px; height: 8px; border-radius: 2px; display: inline-block; }
.wf-summary { font-size: 12px; color: #aaa; padding: 4px 0 8px; }
.mono { font-family: monospace; }
.positive { color: #4caf50; }
.negative { color: #f44336; }
.fold-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 8px; }
.fold-table th { text-align: left; padding: 4px 6px; color: #888; font-weight: 500; border-bottom: 1px solid #333; font-size: 10px; text-transform: uppercase; }
.fold-table td { padding: 4px 6px; border-bottom: 1px solid #1a1a1a; color: #ccc; }
.date-cell { font-size: 10px; color: #888; }
</style>
